// UDS (ISO 14229-1) 0x19 ReadDTCInformation and 0x14 ClearDiagnosticInformation
// request/response decoding for the DTC Decoder.
import { DTC_FORMAT_IDS, UDS_19_SUBFUNCTIONS, UDS_19_DTC_LIST_SUBFUNCTIONS, bytesToHex, hex2 } from './constants';
import { dtcBytesToCode, decodeFtb, describeDtc } from './dtc';
import { decodeStatusByte, statusVerdict, statusContradictions } from './statusByte';

const cell = (start, bytes, label, detail, tone) => ({
  range: bytes.length > 1 ? `Bytes ${start}–${start + bytes.length - 1}` : `Byte ${start}`,
  hex: bytesToHex(bytes),
  label,
  detail,
  tone,
});

const subFnName = (sf) => UDS_19_SUBFUNCTIONS[sf] || 'ISO/SAE reserved or OEM-specific sub-function';

const bitNames = (status) => {
  const names = decodeStatusByte(status).map((b) => b.name);
  return names.length ? names.join(', ') : 'no bits set';
};

export function buildDtcRow(dtcBytes, status, index, supportedMask = 0xff) {
  const code = dtcBytesToCode(dtcBytes);
  return {
    index,
    bytes: dtcBytes,
    code,
    ftb: decodeFtb(dtcBytes[2]),
    status,
    bits: decodeStatusByte(status),
    verdict: statusVerdict(status),
    contradictions: statusContradictions(status, supportedMask),
    info: describeDtc(code),
  };
}

function decodeGroupOfDtc(group) {
  const value = (group[0] << 16) | (group[1] << 8) | group[2];
  if (value === 0xffffff) return 'FF FF FF = all groups (clear every DTC)';
  if (value === 0xffff33) return 'FF FF 33 = emissions-system group (ISO 14229-1 WWH-OBD)';
  return `Either a single DTC (${dtcBytesToCode(group)}-${hex2(group[2])}) or an OEM-defined functional group — ISO 14229-1 leaves group values other than FFFFFF/FFFF33 to the manufacturer.`;
}

// Returns { ok, title, summary, cells, dtcs, warnings, notes, ... } or { ok:false, error }.
export function decodeUdsDtcMessage(bytes) {
  const sid = bytes[0];
  const warnings = [];
  const notes = [];

  // --- 0x14 ClearDiagnosticInformation ---
  if (sid === 0x14) {
    if (bytes.length < 4) return { ok: false, error: `ClearDiagnosticInformation request needs 4 bytes (14 + 3-byte groupOfDTC); got ${bytes.length}.` };
    if (bytes.length > 5) warnings.push(`Expected 4 bytes (or 5 with a MemorySelection byte, ISO 14229-1:2020); ${bytes.length - 5} extra byte(s) ignored.`);
    const cells = [cell(0, [0x14], 'SID', 'ClearDiagnosticInformation request'), cell(1, bytes.slice(1, 4), 'groupOfDTC', decodeGroupOfDtc(bytes.slice(1, 4)))];
    if (bytes.length >= 5) cells.push(cell(4, [bytes[4]], 'MemorySelection', 'Selects which DTC memory to clear (ISO 14229-1:2020, OEM-defined values).'));
    return { ok: true, direction: 'request', title: '0x14 ClearDiagnosticInformation — request', summary: `Clear DTCs: ${decodeGroupOfDtc(bytes.slice(1, 4)).split(' — ')[0]}. The ECU answers 54 if it succeeds (and resets the status of the cleared DTCs, typically to 0x50).`, cells, dtcs: [], warnings, notes };
  }
  if (sid === 0x54) {
    if (bytes.length > 1) warnings.push(`A 0x14 positive response is only the byte 54; ${bytes.length - 1} extra byte(s) ignored.`);
    return { ok: true, direction: 'response', title: '0x54 — ClearDiagnosticInformation positive response', summary: 'The ECU cleared the requested DTC group. Cleared DTCs typically report status 0x50 (tests not completed since the clear / this cycle) until their monitors run again.', cells: [cell(0, [0x54], 'Response SID', '0x14 + 0x40 — clear accepted')], dtcs: [], warnings, notes };
  }

  // --- 0x19 request ---
  if (sid === 0x19) {
    if (bytes.length < 2) return { ok: false, error: 'ReadDTCInformation request needs at least a sub-function byte (e.g. 19 02 FF).' };
    const sf = bytes[1] & 0x7f;
    const cells = [cell(0, [0x19], 'SID', 'ReadDTCInformation request'), cell(1, [bytes[1]], 'Sub-function', `0x${hex2(sf)} ${subFnName(sf)}${bytes[1] & 0x80 ? ' (suppress positive response bit set)' : ''}`)];
    let summary = `Request: ${subFnName(sf)}.`;
    if (sf === 0x01 || sf === 0x02) {
      if (bytes.length < 3) return { ok: false, error: `Sub-function 0x${hex2(sf)} needs a DTCStatusMask byte after it (e.g. 19 ${hex2(sf)} FF).` };
      cells.push(cell(2, [bytes[2]], 'DTCStatusMask', bitNames(bytes[2])));
      summary = `Request: ${sf === 0x01 ? 'count' : 'list'} the DTCs whose status AND 0x${hex2(bytes[2])} is non-zero (${bitNames(bytes[2])}).`;
      if (bytes.length > 3) warnings.push(`${bytes.length - 3} unexpected extra byte(s) after the status mask.`);
    } else if (sf === 0x04 || sf === 0x06) {
      if (bytes.length < 6) return { ok: false, error: `Sub-function 0x${hex2(sf)} needs 3 DTC bytes and a record number (e.g. 19 ${hex2(sf)} 03 01 00 ${sf === 0x04 ? '01' : 'FF'}); got ${bytes.length} bytes.` };
      const code = dtcBytesToCode(bytes.slice(2, 4));
      cells.push(cell(2, bytes.slice(2, 5), 'DTC', `${code}, FTB 0x${hex2(bytes[4])}`));
      const rec = bytes[5];
      cells.push(cell(5, [rec], sf === 0x04 ? 'DTCSnapshotRecordNumber' : 'DTCExtDataRecordNumber', rec === 0xff ? '0xFF = all records' : `Record 0x${hex2(rec)} (record numbering is OEM-defined)`));
      summary = `Request: ${sf === 0x04 ? 'snapshot (freeze-frame)' : 'extended data'} record ${rec === 0xff ? '(all)' : `0x${hex2(rec)}`} for ${code}.`;
    } else if (sf === 0x0a) {
      summary = 'Request: list every DTC the ECU supports, with its current status.';
    } else if (bytes.length > 2) {
      cells.push(cell(2, bytes.slice(2), 'Parameters', 'Not decoded for this sub-function'));
    }
    return { ok: true, direction: 'request', title: `0x19 ReadDTCInformation — request (${subFnName(sf)})`, summary, cells, dtcs: [], warnings, notes };
  }

  // --- 0x59 positive response ---
  if (sid !== 0x59) return { ok: false, error: `0x${hex2(sid)} is not a UDS 0x19/0x14 message.` };
  if (bytes.length < 2) return { ok: false, error: '59 alone is incomplete — a 0x19 positive response echoes the sub-function next (e.g. 59 02 …).' };
  const sf = bytes[1];
  const cells = [cell(0, [0x59], 'Response SID', '0x19 + 0x40 — ReadDTCInformation positive response'), cell(1, [sf], 'Sub-function', `0x${hex2(sf)} ${subFnName(sf)}`)];
  const base = { ok: true, direction: 'response', title: `0x59 ReadDTCInformation — ${subFnName(sf)}`, cells, dtcs: [], warnings, notes };

  if (sf === 0x01 || sf === 0x07) {
    if (bytes.length < 6) return { ok: false, error: `0x59 ${hex2(sf)} response needs 6 bytes (59 ${hex2(sf)} <availabilityMask> <formatId> <count hi> <count lo>); got ${bytes.length}.` };
    const fmt = DTC_FORMAT_IDS[bytes[3]];
    const count = (bytes[4] << 8) | bytes[5];
    cells.push(cell(2, [bytes[2]], sf === 0x01 ? 'DTCStatusAvailabilityMask' : 'DTCStatusAvailabilityMask', `Bits this ECU supports: ${bitNames(bytes[2])}`));
    cells.push(cell(3, [bytes[3]], 'DTCFormatIdentifier', fmt ? `${fmt.name} (${fmt.standard})` : 'Reserved value'));
    cells.push(cell(4, bytes.slice(4, 6), 'DTCCount', `${count} DTC${count === 1 ? '' : 's'} match the requested mask`));
    if (bytes.length > 6) warnings.push(`${bytes.length - 6} unexpected extra byte(s) after the DTC count.`);
    return { ...base, summary: `${count} DTC${count === 1 ? '' : 's'} matched the status mask. DTC format: ${fmt ? fmt.name : `reserved 0x${hex2(bytes[3])}`}.`, availabilityMask: bytes[2], formatId: bytes[3], format: fmt || null, count };
  }

  if (UDS_19_DTC_LIST_SUBFUNCTIONS.has(sf)) {
    if (bytes.length < 3) return { ok: false, error: `0x59 ${hex2(sf)} response needs the DTCStatusAvailabilityMask byte after the sub-function.` };
    const mask = bytes[2];
    cells.push(cell(2, [mask], 'DTCStatusAvailabilityMask', `Status bits this ECU supports: ${bitNames(mask)}`));
    const body = bytes.slice(3);
    const complete = Math.floor(body.length / 4);
    const dtcs = [];
    for (let i = 0; i < complete; i += 1) {
      const rec = body.slice(i * 4, i * 4 + 4);
      const row = buildDtcRow(rec.slice(0, 3), rec[3], i + 1, mask);
      dtcs.push(row);
      cells.push(cell(3 + i * 4, rec, `DTC #${i + 1}`, `${row.code}, FTB 0x${row.ftb.hex}, status 0x${hex2(rec[3])} — ${row.verdict.primary.title}`, 'dtc'));
    }
    const leftover = body.length % 4;
    if (leftover) {
      const tail = body.slice(complete * 4);
      warnings.push(`The last record is truncated: ${leftover} byte${leftover === 1 ? '' : 's'} (${bytesToHex(tail)}) instead of 4 (3 DTC bytes + 1 status byte). The ${complete} complete record${complete === 1 ? ' was' : 's were'} decoded; the incomplete one was not.`);
      cells.push(cell(3 + complete * 4, tail, 'Truncated record', 'Incomplete — not decoded', 'warn'));
    }
    notes.push('This response does not say which DTC format the ECU uses (0x19 01 does). Codes are shown with the common SAE J2012 packing (P/C/B/U + FTB); if the ECU reports a J1939 or ISO 11992-4 format, that decoding does not apply.');
    const unsupportedBits = decodeStatusByte(~mask & 0xff).map((b) => b.name);
    if (mask !== 0xff && unsupportedBits.length) notes.push(`Availability mask 0x${hex2(mask)}: this ECU does not support ${unsupportedBits.join(', ')}. Those bits always read 0 and carry no information.`);
    const summary = dtcs.length ? `${dtcs.length} DTC${dtcs.length === 1 ? '' : 's'} reported: ${dtcs.map((d) => `${d.code} (0x${hex2(d.status)})`).join(', ')}.` : 'No DTCs reported — no stored DTC matched the requested status mask.';
    return { ...base, summary, dtcs, availabilityMask: mask };
  }

  if (sf === 0x04 || sf === 0x06) {
    if (bytes.length < 6) return { ok: false, error: `0x59 ${hex2(sf)} response needs at least 6 bytes (59 ${hex2(sf)} + 3 DTC bytes + status); got ${bytes.length}.` };
    const row = buildDtcRow(bytes.slice(2, 5), bytes[5], 1);
    cells.push(cell(2, bytes.slice(2, 5), 'DTC', `${row.code}, FTB 0x${row.ftb.hex}`, 'dtc'));
    cells.push(cell(5, [bytes[5]], 'DTC status', `0x${hex2(bytes[5])} — ${row.verdict.primary.title}`));
    const rest = bytes.slice(6);
    if (!rest.length) {
      notes.push('No record data follows — the ECU has no stored record of that number for this DTC.');
    } else if (sf === 0x04) {
      cells.push(cell(6, [rest[0]], 'DTCSnapshotRecordNumber', `Record 0x${hex2(rest[0])}`));
      if (rest.length >= 2) cells.push(cell(7, [rest[1]], 'Number of identifiers', `${rest[1]} DID${rest[1] === 1 ? '' : 's'} in this record`));
      if (rest.length >= 4) cells.push(cell(8, rest.slice(2, 4), 'First DID', `0x${hex2(rest[2])}${hex2(rest[3])}`));
      if (rest.length > 4) cells.push(cell(10, rest.slice(4), 'Record data (raw)', 'DID data lengths are OEM-defined — shown raw'));
      notes.push('Snapshot record layout: record number, number of DIDs, then DID + data for each. The data length of each DID (and so where the next DID or record starts) is defined by the OEM, so everything after the first DID is shown raw.');
    } else {
      cells.push(cell(6, [rest[0]], 'DTCExtDataRecordNumber', `Record 0x${hex2(rest[0])}`));
      if (rest.length > 1) cells.push(cell(7, rest.slice(1), 'Extended data (raw)', 'Layout (occurrence/ageing counters, etc.) is OEM-defined'));
      notes.push('Extended data records (e.g. occurrence counter, ageing counter, fault detection counter) have OEM-defined lengths and meanings, so they are shown raw.');
    }
    return { ...base, summary: `${sf === 0x04 ? 'Snapshot' : 'Extended data'} for ${row.code} (status 0x${hex2(bytes[5])}).`, dtcs: [row] };
  }

  if (bytes.length > 2) cells.push(cell(2, bytes.slice(2), 'Response data (raw)', 'This sub-function\'s layout is not decoded by this tool'));
  return { ...base, summary: `Positive response for ${subFnName(sf)} — layout not decoded by this tool.` };
}
